/*
 * The problems an uploaded freight cost xlsx file can hold.
 * Every kind of problem has exactly one wording, listed in ERROR_MESSAGES.
 * The collector builds the text from the kind it is given.
 */

import {
  FreightCostImportError,
  FreightCostImportErrorType
} from '../../types/freightCost/freightCostXlsx';
import { DISTANCE_COLUMN_HEADER, OVER_RANGES_LABEL } from './freightCostFormat';

/**
 * Where a problem was found.
 * Empty for a problem of the whole file.
 */
type ErrorPosition = {
  // name of the sheet
  sheet?: string;
  // excel reference of the cell, when the problem belongs to an exact cell
  cell?: string;
};

/**
 * The wording of every kind of problem, with the values it needs.
 * Record keeps the list complete:
 * a new kind of problem does not compile until it gets its text here.
 */
const ERROR_MESSAGES = {
  [FreightCostImportErrorType.duplicatedDistance]: (distance?: number) =>
    `${
      distance === undefined
        ? `A "${OVER_RANGES_LABEL}" jelű sor`
        : `A(z) ${distance} km-es sávhatár`
    } ` + 'több sorban is szerepel.',

  [FreightCostImportErrorType.duplicatedFactory]: (factoryId: string) =>
    `A(z) "${factoryId}" gyárhoz több munkalap is tartozik.`,

  [FreightCostImportErrorType.duplicatedTypeHeader]: (header: string) =>
    `A(z) "${header}" fuvardíj-kategória több oszlopban is szerepel.`,

  [FreightCostImportErrorType.emptyFile]: () => 'A fájl egyetlen munkalapot sem tartalmaz.',

  [FreightCostImportErrorType.emptyTypeColumn]: () =>
    'A fuvardíj-kategória oszlopában egyetlen díj sincs kitöltve. Ha ezt a kategóriát nem ' +
    'szeretné módosítani, törölje a teljes oszlopot a fejlécével együtt.',

  [FreightCostImportErrorType.emptyTypeHeader]: () =>
    'Az oszlopban vannak kitöltött cellák, de a fejléce üres. Írja be a fuvardíj-kategória ' +
    'fejlécét, vagy törölje a teljes oszlopot.',

  [FreightCostImportErrorType.invalidCost]: (value: string) =>
    `A fuvardíj csak nulla vagy annál nagyobb egész szám lehet, a megadott érték: "${value}".`,

  [FreightCostImportErrorType.invalidDistance]: (value: string) =>
    `A km távolság csak pozitív egész szám vagy a "${OVER_RANGES_LABEL}" jel lehet, ` +
    `a megadott érték: "${value}".`,

  [FreightCostImportErrorType.invalidPayload]: () =>
    'A feltöltött adatok szerkezete hibás. Szerkessze a fájlt, majd próbálja újra.',

  [FreightCostImportErrorType.missingDistance]: () =>
    'A km távolság nincs megadva, pedig a sorban van fuvardíj.',

  [FreightCostImportErrorType.missingDistanceHeader]: () =>
    `A munkalap első oszlopának fejléce "${DISTANCE_COLUMN_HEADER}" kell legyen.`,

  [FreightCostImportErrorType.missingTypeColumns]: () =>
    'A munkalapon csak km távolságok vannak, egyetlen fuvardíj-kategória oszlopa sincs ' +
    'kitöltve. Ha ezzel a gyárral nem szeretne semmit tenni, hagyja üresen a munkalapot.',

  [FreightCostImportErrorType.nothingToImport]: () =>
    'A fájl egyetlen gyárhoz sem tartalmaz fuvardíjat. Töltse ki legalább egy gyár munkalapját.',

  [FreightCostImportErrorType.tooManyRows]: (rowCount: number, maxRowCount: number) =>
    `A fájl ${rowCount} kitöltött sort tartalmaz, legfeljebb ${maxRowCount} kezelhető.`,

  [FreightCostImportErrorType.unknownFactory]: () =>
    'A munkalap neve nem egy létező gyár azonosítója.',

  [FreightCostImportErrorType.unknownTypeHeader]: (header: string, validHeaders: string[]) =>
    `A(z) "${header}" fejléc nem ismert fuvardíj-kategória. Az érvényes fejlécek: ` +
    `${validHeaders.join(', ')}.`
} satisfies Record<FreightCostImportErrorType, (...params: never[]) => string>;

/** The values the wording of a kind of problem needs */
type ErrorMessageParams<T extends FreightCostImportErrorType> = Parameters<
  (typeof ERROR_MESSAGES)[T]
>;

/** Gathers the problems of one imported file */
export class FreightCostImportErrors {
  readonly list: FreightCostImportError[] = [];

  /**
   * Puts a problem on the list, worded by its kind.
   *
   * @param {FreightCostImportErrorType} type kind of the problem
   * @param {ErrorPosition} position sheet and cell the problem belongs to,
   * empty for the whole file
   * @param {ErrorMessageParams} params the values the wording of this kind needs
   */
  add<T extends FreightCostImportErrorType>(
    type: T,
    position: ErrorPosition,
    ...params: ErrorMessageParams<T>
  ): void {
    const buildMessage = ERROR_MESSAGES[type] as (...args: ErrorMessageParams<T>) => string;

    this.list.push({ type, message: buildMessage(...params), ...position });
  }
}
